/**
 * React Query hook for case matters list v2 (`/list-case-matters-v2`).
 * Response shape matches Billing V2 RDS lists: `total_count`, `data`, `page_count`, `page`, `limit`, `has_next`, `has_previous`.
 */

import { useQuery } from '@tanstack/react-query';
import { listCaseMattersV2 } from 'services/CaseServices';
import { queryKeys } from '../config/queryClient';

/** Map v2 row shape — list API uses `case_matter_id`; map ids for edit/actions. */
const mapCaseMatterRow = (row) => {
  if (!row || typeof row !== 'object') return row;
  const id = row.case_matter_id ?? row.sk ?? row.pk;
  return {
    ...row,
    pk: row.pk ?? id,
    sk: row.sk ?? id,
  };
};

const normalizeListResponse = (raw, page, limit) => {
  const list = raw?.data ?? raw?.['case-matter'] ?? [];
  const rows = Array.isArray(list) ? list.map(mapCaseMatterRow) : [];
  const total_count = Number(raw?.total_count ?? 0) || 0;
  const page_count = Number(raw?.page_count) > 0 ? Number(raw.page_count) : limit > 0 ? Math.max(1, Math.ceil(total_count / limit)) : 1;

  return {
    rows,
    total_count,
    page_count,
    page: raw?.page ?? page,
    limit: raw?.limit ?? limit,
    has_next: Boolean(raw?.has_next),
    has_previous: Boolean(raw?.has_previous),
  };
};

/**
 * @param {Object} options
 * @param {number} [options.limit=10]
 * @param {number} [options.page=1]
 * @param {string} [options.sort_by='']
 * @param {string} [options.sort_order='']
 * @param {boolean} [options.enabled=true]
 * @returns {{ caseMatterData, caseMatterRows, totalRecords, totalPages, currentPage, hasNext, hasPrevious, isLoading, isFetching, isError, error, refetch }} — `isLoading` / `isFetching` match `useInvoiceListV2Query` (use both for table loading when using `keepPreviousData`).
 */
export const useCaseMatterListV2Query = ({ limit = 10, page = 1, sort_by = '', sort_order = '', enabled = true } = {}) => {
  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey: queryKeys.caseMatters.settingsListV2(page, limit, sort_by, sort_order),
    queryFn: async () => {
      const response = await listCaseMattersV2({
        limit,
        page,
        sort_by,
        sort_order,
      });
      return normalizeListResponse(response?.data, page, limit);
    },
    enabled,
    keepPreviousData: true,
    staleTime: 2 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 2,
  });

  return {
    caseMatterData: data,
    caseMatterRows: data?.rows ?? [],
    /** Same meaning as InvoiceV2 list — for EllipsisPagination `totalRecords` */
    totalRecords: data?.total_count ?? 0,
    totalPages: data?.page_count ?? 1,
    currentPage: data?.page ?? page,
    hasNext: data?.has_next ?? false,
    hasPrevious: data?.has_previous ?? false,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  };
};
